import { useEffect, useMemo, useState } from 'react';
import { proService } from '../services';

/**
 * Pick a time you can actually have.
 *
 * A free-text range invites a 47-minute booking starting at 9:13, which nobody
 * sells. The server generates real slots from the pro's hours — minus busy time,
 * minus buffers, minus anything inside their notice period — and the client
 * picks one. That's the difference between a range picker and a booking
 * calendar, and it's how Calendly, Cal.com and Acuity all work.
 *
 * Times are shown in the READER'S zone, because that's the one they'll turn up
 * in, with the pro's own zone named underneath whenever the two differ. A slot
 * that reads "2pm" to one and "5pm" to the other has to say so.
 */
function readerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/** A slot's wall-clock minute in the PRO's zone, as an instant. */
export function slotInstant(dateIso, minute, vendorZone) {
  const [y, m, d] = dateIso.split('-').map(Number);
  const hour = Math.floor(minute / 60);
  const min = minute % 60;
  let guess = Date.UTC(y, m - 1, d, hour, min, 0);
  if (vendorZone) {
    // Correct the guess by the offset the zone actually had at that moment, so
    // this stays right on both sides of a daylight-saving change.
    for (let i = 0; i < 2; i += 1) {
      const seen = new Date(guess).toLocaleString('en-US', { timeZone: vendorZone, hour12: false });
      const asUtc = Date.parse(`${seen} UTC`.replace(',', ''));
      if (Number.isNaN(asUtc)) break;
      guess += Date.UTC(y, m - 1, d, hour, min, 0) - asUtc;
    }
  }
  return new Date(guess);
}

const shortDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

export default function SlotPicker({ slug, value, onChange, serviceId = null }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDate, setOpenDate] = useState(null);
  const reader = useMemo(readerZone, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await proService.slots(slug, null, 30, serviceId);
      if (cancelled) return;
      const list = (r?.success && r.data) || [];
      setDays(list);
      setOpenDate(list[0]?.date || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // Re-asks when the service changes: its length decides which slots even fit.
  }, [slug, serviceId]);

  if (loading) {
    return <p className="font-mono text-[11px] uppercase tracking-wide text-gray-500">Finding times…</p>;
  }

  if (days.length === 0) {
    return (
      <p className="border-2 border-black bg-gray-50 px-3 py-2 font-mono text-[11px] leading-relaxed">
        No open times in the next month. You can still send a request below and
        suggest a time that works for you.
      </p>
    );
  }

  const vendorZone = days[0]?.timeZoneId;
  const zonesDiffer = vendorZone && vendorZone !== reader;
  const day = days.find((d) => d.date === openDate) || days[0];

  return (
    <div className="border-2 border-black">
      {/* Days that actually have something free. A day with nothing open is not
          worth a row — it just makes the list longer to read. */}
      <div className="flex gap-1 overflow-x-auto border-b-2 border-black p-2" style={{ scrollbarWidth: 'none' }}>
        {days.map((d) => (
          <button
            key={d.date}
            type="button"
            onClick={() => setOpenDate(d.date)}
            aria-label={`${shortDate(d.date)}, ${d.starts.length} time${d.starts.length === 1 ? '' : 's'} open`}
            className={`flex-shrink-0 border-2 border-black px-2.5 py-1.5 text-left font-mono uppercase ${
              d.date === day.date ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            <span className="block text-[10px] font-bold tracking-wide">{shortDate(d.date)}</span>
            {/* On its own line: inline, "SEP 1 15" reads as part of the date. */}
            <span className={`block text-[9px] tracking-wide ${d.date === day.date ? 'text-white/60' : 'text-gray-500'}`}>
              {d.starts.length} {d.starts.length === 1 ? 'time' : 'times'}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5 p-2">
        {day.starts.map((minute) => {
          const at = slotInstant(day.date, minute, vendorZone);
          const label = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          const end = new Date(at.getTime() + day.durationMinutes * 60000);
          const chosen = value && value.date === day.date && value.startMinute === minute;
          return (
            <button
              key={minute}
              type="button"
              aria-pressed={chosen}
              onClick={() => onChange?.({
                date: day.date,
                startMinute: minute,
                startsAt: at.toISOString(),
                endsAt: end.toISOString(),
                label,
              })}
              className={`border-2 border-black px-2.5 py-1.5 font-mono text-[11px] font-bold ${
                chosen ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <p className="border-t-2 border-black px-3 py-2 font-mono text-[10px] leading-relaxed text-gray-500">
        {day.durationMinutes} minutes · times shown in your own time zone
        {zonesDiffer ? ` (they're in ${vendorZone.split('/').pop().replace('_', ' ')})` : ''}.
      </p>
    </div>
  );
}
